<template>
  <VModal :showing="open" container-classes="max-w-2xl" @update:showing="onShareDialogOpenChange">
    <template #default>
      <div class="flex flex-col gap-6 overflow-visible relative">
        <!-- Loading overlay -->
        <div v-if="loading" class="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
        </div>
        <!-- Header -->
        <div>
          <div data-intercom="share-presentation-dialog-title" class="text-lg font-semibold">Share 3D Presentation</div>
          <p class="text-sm text-muted-foreground">
            Invite others to view or collaborate on this 3D Presentation by sharing the link or managing access below.
          </p>
        </div>

        <!-- Invite -->
        <div v-if="canManageAccess" class="flex justify-between gap-2 relative">
          <DeckHeaderShareInvite
            v-model:loading="loading"
            :deck="deck"
            :existing-users="existingInviteTargets"
            @invite-requested="handleInviteRequested"
          />
        </div>

        <!-- Access list -->
        <div>
          <div class="text-sm font-semibold mb-2">People with Access</div>
          <div
            class="space-y-4 h-52 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-300 hover_scrollbar-thumb-gray-400"
          >
            <div v-for="user in sortedUsers" :key="user.key" class="grid grid-cols-[3rem_3fr_1fr] items-center gap-2">
              <div class="w-12 h-12 flex justify-center items-center text-white rounded-full bg-gray-900">
                <span>{{ user.initials }}</span>
              </div>
              <div class="text-sm min-w-0 flex-1">
                <div class="flex items-center gap-2 min-w-0">
                  <span class="truncate block">{{ user.name }} {{ user.isCurrentUser ? '(you)' : '' }}</span>
                  <span
                    v-if="user.isPending"
                    class="shrink-0 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-700"
                  >
                    Pending
                  </span>
                </div>
                <span v-if="user.email && user.email !== user.name" class="text-muted-foreground truncate block">{{ user.email }}</span>
              </div>
              <div class="flex items-center justify-end gap-2 mr-2 min-w-0">
                <VDropdown
                  :options="roleOptions"
                  :model-value="getRoleOption(sharePickerRoleFromApiRole(user.role))"
                  :disabled="shouldDisableRoleDropdown(user)"
                  size="sm"
                  class="!w-24"
                  @update:model-value="handleUserRoleSelect({ user, role: $event.id })"
                >
                  <template #current-single>
                    <span class="block text-primary">
                      {{
                        isExternalUser(user)
                          ? displayDeckRoleLabel(user.role)
                          : deck.created_by?.id != null && user.user_id === deck.created_by.id
                            ? 'Owner'
                            : displayDeckRoleLabel(user.role)
                      }}
                    </span>
                  </template>
                  <template #option="{ item }">
                    <VDropdownItem :item="item.name" />
                  </template>
                </VDropdown>

                <div class="w-6 h-6 flex items-center justify-center">
                  <VTooltip direction="left">
                    <template #content>
                      <div class="whitespace-nowrap">Resend invitation</div>
                    </template>
                    <VButton
                      v-if="canResendInvite(user)"
                      variant="ghost"
                      size="sm"
                      :disabled="loading"
                      aria-label="Resend invite"
                      class="w-6 h-6 p-0 flex items-center justify-center"
                      @click="handleResendInvite(user)"
                    >
                      <FontAwesomeIcon icon="envelope" class="w-3 h-3" />
                    </VButton>
                  </VTooltip>
                </div>

                <div class="w-6 h-6 flex items-center justify-center">
                  <VButton
                    v-if="canRemoveUser(user)"
                    variant="ghost"
                    size="sm"
                    :disabled="loading"
                    class="rounded-full border w-6 h-6 p-0 flex items-center justify-center"
                    @click="handleDeleteUser(user)"
                  >
                    <FontAwesomeIcon icon="times" class="w-3 h-3" />
                  </VButton>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- General Access -->
        <div>
          <div data-intercom="share-presentation-dialog-general-access-title" class="text-sm font-semibold mb-2">General Access</div>

          <div class="flex justify-between items-center gap-4">
            <VDropdown
              :options="generalAccessDropdownOptions"
              :model-value="getGeneralAccessOption(selectedGeneralAccess)"
              :disabled="!canManageAccess"
              class="w-72 h-8 mb-4"
              dropdown-class="z-50"
              @update:model-value="handleGeneralAccessSelect(getGeneralAccessOptionById($event.id))"
            >
              <template #current-single="{ item }">
                <div class="flex items-center overflow-hidden" :title="item.name">
                  <div class="mr-2 w-4 h-4 flex items-center">
                    <AccessTypeIcon :access-type="item.id as DeckBaseAccess" class="!w-4 !h-4" />
                  </div>
                  <span class="whitespace-nowrap truncate">{{ item.name }}</span>
                </div>
              </template>
              <template #option="{ item }">
                <div class="flex items-center px-3 py-0.5 overflow-hidden" :title="item.name">
                  <div class="ml-0.5 mr-2 w-4 h-4 flex items-center justify-center">
                    <AccessTypeIcon :access-type="item.id as DeckBaseAccess" class="!w-4 !h-4" />
                  </div>
                  <span class="whitespace-nowrap truncate text-sm">{{ item.name }}</span>
                </div>
              </template>
            </VDropdown>

            <VDropdown
              v-if="selectedGeneralAccess?.value !== DeckBaseAccess.Private"
              :options="companyAccessRoleDropdownOptions"
              :model-value="getRoleOption(deckPermissionUserRole)"
              :disabled="!canManageAccess || selectedGeneralAccess?.value === DeckBaseAccess.Public"
              class="w-24 h-8 mb-4 mr-10"
              dropdown-class="z-50"
              @update:model-value="handleDeckPermissionAccess($event.id as DeckBaseAccess)"
            >
              <template #current-single="{ item }">
                <span>{{ item.name }}</span>
              </template>
              <template #option="{ item }">
                <div class="px-4">
                  <span>{{ item.name }}</span>
                </div>
              </template>
            </VDropdown>
          </div>
        </div>

        <div
          v-if="showMultipleOwnersBanner"
          class="flex items-center gap-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800"
        >
          <FontAwesomeIcon icon="exclamation-triangle" class="w-8 h-8" />
          <span class="text-sm">Add a company admin or another editor to avoid potential access blockages.</span>
        </div>

        <!-- Footer -->
        <div class="flex justify-between">
          <VCopyButton
            :copy-text="shareUrl"
            variant="outline"
            @click="$analytics.track('deckShareDialogCopyLink', { clickedFromDialog: true })"
          >
            <FontAwesomeIcon icon="copy" class="w-4 h-4 mr-2" />
            Copy Link
          </VCopyButton>
          <VButton :disabled="loading || !accessChangeQueue.length" @click="onSaveChanges">Save Changes</VButton>
        </div>
      </div>
    </template>
  </VModal>

  <ModalsAlert
    :showing="unsavedChangesAlertShowing"
    variant="warning"
    confirmation-label="Discard"
    @update:showing="onUnsavedChangesAlertUpdate"
    @confirm="onUnsavedChangesDiscard"
  >
    <template #title>Unsaved changes</template>
    You have unsaved sharing changes. Close without saving?
  </ModalsAlert>

  <ModalsAlert
    :showing="editorDowngradeAlertShowing"
    variant="warning"
    confirmation-label="Change to Viewer"
    @update:showing="onEditorDowngradeAlertUpdate"
    @confirm="onEditorDowngradeConfirm"
  >
    <template #title>Remove Editor Access</template>
    By changing your access to Viewer you will not be able to revert back to Editor once saved.
  </ModalsAlert>

  <!-- Embed Usage Warning Modal -->
  <EditorDeckModalsEmbedDeckUsage
    :showing="embedUsageWarning.showing"
    :decks-affected="embedUsageWarning.decksAffected"
    :public-decks="embedUsageWarning.publicDecks"
    @cancel="embedUsageWarning.cancel"
    @confirm="embedUsageWarning.confirm"
  />

  <!-- Drill Data Access Type Warning Modal -->
  <VModal :showing="drillDataAccessTypeWarning.showing" @update:showing="drillDataAccessTypeWarning.showing = $event">
    <template #header>
      <h2 class="mb-2 text-xl">Drill Data Access Warning</h2>
      <p class="m-0 text-sm font-light text-gray-800">This 3D Presentation contains drill data that will be affected by this change.</p>
    </template>
    <template #default>
      <div class="space-y-4">
        <p class="text-sm">
          This 3D presentation contains drill data that is currently private or secure. Making this presentation public will also make the
          drill data public.
        </p>
        <div v-if="drillDataAccessTypeWarning.drillData.length" class="space-y-2">
          <h3 class="text-sm font-semibold">Affected Drill Data:</h3>
          <ul class="text-sm space-y-1">
            <li v-for="drill in drillDataAccessTypeWarning.drillData" :key="drill.id">
              {{ drill.name }}
            </li>
          </ul>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="flex justify-end gap-2">
        <VButton variant="outline" @click="drillDataAccessTypeWarning.cancel">Cancel</VButton>
        <VButton variant="outline" @click="drillDataAccessTypeWarning.secondaryAction">Continue Without Making Drill Data Public</VButton>
        <VButton @click="drillDataAccessTypeWarning.confirm">Make Drill Data Public & Continue</VButton>
      </div>
    </template>
  </VModal>
</template>

<script setup lang="ts">
import { DeckBaseAccess, DeckPermissionsUserRole } from '~~/entities/deck'
import type { APIDeckPermissions, APIDeckPermissionsRole, APIDeckExternalShare, APISecureLink } from '~~/entities/api'
import {
  apiRoleFromSharePickerRole,
  displayDeckRoleLabel,
  isElevatedDeckRole,
  shareDialogPickerRoles,
  sharePickerRoleFromApiRole,
} from '~~/lib/deck-share-roles'
// import { getParticipantColor, cleanupParticipantColors } from '~~/lib/participant-colors'
import * as Sentry from '@sentry/vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import type { DeepReadonly } from 'vue'
import type { EditDeck } from '~/composables/useEditDeck'
import type { DrillDataSetManager } from '~/lib/three-d-resources/drill-data-set-manager'
import type { DrillDataSet } from '~/entities/drillLabel'
import type { Drill } from '~/entities/drill'
import type { DeckWithEmbed } from '~/entities/embed'
import type { Deck, Slide } from '~/entities/deck'

interface User extends APIDeckPermissionsRole {
  key: string
  initials: string
  isCurrentUser: boolean
  avatarColor: string
  isPending: boolean
}

interface ExternalInviteUser {
  key: string
  secureLinkId: string | null
  name: string
  email: string
  role: DeckPermissionsUserRole
  initials: string
  isCurrentUser: false
  avatarColor: string
  isPending: boolean
  inviteType: 'external'
}

type ShareAccessUser = User | ExternalInviteUser

interface InviteRequestedPayload {
  type: 'internal' | 'external'
  email: string
  user?: {
    id: number
    name: string
    email_address?: string | null
  }
}

interface GeneralAccessOption {
  label: string
  value: DeckBaseAccess
}

interface AccessChangeQueueItem {
  key: string
  userId: number | null
  type: AccessChangeType
  promise: () => Promise<unknown>
  onSuccess?: (value: unknown) => void
}

enum AccessChangeType {
  Upsert = 'upsert',
  Delete = 'delete',
  GeneralAccess = 'general-access',
  SecureLinkCreate = 'secure-link-create',
  SecureLinkDelete = 'secure-link-delete',
}

const props = defineProps<{
  deck: DeepReadonly<EditDeck>
  slides?: DeepReadonly<Slide[]>
  drillDataSetManager?: DrillDataSetManager
}>()

const open = defineModel<boolean>('open', { default: false })

const emit = defineEmits<{
  'refresh-deck': []
}>()

let editorDowngradeResolver: ((value: boolean) => void) | null = null

// const { selectedCompany } = useCompanies()
const {
  getDeckPermissions,
  updateDeckPermissions,
  upsertDeckUserPermissions,
  deleteDeckUserPermissions,
  requestDeckSecureLink,
  resendDeckSecureLink,
  deleteDeckSecureLink,
} = useDeckPermissions()
const { state: userState, hasRole } = useUser()
const { setNotification } = useGlobalNotification()
const { $analytics } = useNuxtApp()

const router = useRouter()
const loading = ref(false)
const users = ref<User[]>([])
const externalUsers = ref<ExternalInviteUser[]>([])
const savedExternalUsers = ref<ExternalInviteUser[]>([])
const permissions = ref<APIDeckPermissions | null>(null)
const selectedGeneralAccess = ref<GeneralAccessOption | null>(null)
const userDeckRole = ref<DeckPermissionsUserRole | null>(null)
const pendingAccessChange = ref<GeneralAccessOption | null>(null)
const accessChangeQueue = ref<AccessChangeQueueItem[]>([])
const unsavedChangesAlertShowing = ref(false)
const editorDowngradeAlertShowing = ref(false)
const embeds = ref<any[]>([])
const deckPermissionUserRole = ref<DeckPermissionsUserRole>(DeckPermissionsUserRole.Viewer)
const permissionRoleMap = new Map<DeckBaseAccess, DeckPermissionsUserRole>([
  [DeckBaseAccess.CompanyView, DeckPermissionsUserRole.Viewer],
  [DeckBaseAccess.CompanyEdit, DeckPermissionsUserRole.Editor],
])
const embedUsageWarning = reactive<{
  showing: boolean
  decksAffected: DeckWithEmbed[]
  publicDecks: any[]
  cancel(): void
  confirm(): void
}>({
  showing: false,
  decksAffected: [],
  publicDecks: [],
  cancel: () => {},
  confirm: () => {},
})
const drillDataAccessTypeWarning = reactive<{
  showing: boolean
  drillData: Drill[]
  cancel(): void
  confirm(): void
  secondaryAction(): void
}>({
  showing: false,
  drillData: [],
  cancel: () => {},
  confirm: () => {},
  secondaryAction: () => {},
})

const shareUrl = computed(() => {
  if (process.client) {
    const route = router.resolve({
      name: 'editor-decks-id',
      params: { id: `${props.deck.id}` },
    })

    return `${window.location.origin}${route.fullPath}`
  }

  return ''
})

const generalAccessOptions = computed<GeneralAccessOption[]>(() => [
  {
    label: 'Only people invited',
    value: DeckBaseAccess.Private,
  },
  {
    label: `Everyone at ${props.deck.company?.name ?? 'your company'}`,
    value: DeckBaseAccess.CompanyView,
  },
  {
    label: 'Anyone with the link',
    value: DeckBaseAccess.Public,
  },
])

// Per-user role picker: only Viewer / Editor in UI; API maps Editor → Owner via apiRoleFromSharePickerRole
const roleOptions = computed(() =>
  shareDialogPickerRoles.map(role => ({
    id: role,
    name: displayDeckRoleLabel(role),
  })),
)

const companyAccessRoleDropdownOptions = computed(() => [
  { id: DeckBaseAccess.CompanyView, name: displayDeckRoleLabel(DeckPermissionsUserRole.Viewer) },
  { id: DeckBaseAccess.CompanyEdit, name: displayDeckRoleLabel(DeckPermissionsUserRole.Editor) },
])

// Convert general access options to dropdown format
const generalAccessDropdownOptions = computed(() =>
  generalAccessOptions.value.map(option => ({
    id: option.value,
    name: option.label,
    value: option.value,
  })),
)

const existingInviteTargets = computed(() => [
  ...users.value.map(user => ({ user_id: user.user_id, email: user.email })),
  ...externalUsers.value.map(user => ({ user_id: null, email: user.email })),
])

function getInitials(value?: string | null) {
  const source = value?.trim()
  if (!source) return '--'

  const parts = source.split(/\s+/).filter(Boolean)
  if (parts.length > 1) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  }

  return source.slice(0, 2).toUpperCase()
}

function createPermissionUser(role: APIDeckPermissionsRole, isPending = false): User {
  return {
    ...role,
    key: `user:${role.user_id}`,
    initials: getInitials(role.name || role.email),
    isCurrentUser: role.user_id === userState.value.user?.id,
    avatarColor: '',
    isPending,
  }
}

function createExternalInviteUser({
  email,
  secureLinkId = null,
  name,
  isPending = false,
}: {
  email: string
  secureLinkId?: string | null
  name?: string | null
  isPending?: boolean
}): ExternalInviteUser {
  const normalizedEmail = email?.trim().toLowerCase() || ''
  const displayName = name?.trim() || normalizedEmail

  return {
    key: secureLinkId ? `secure-link:${secureLinkId}` : `secure-link:create:${normalizedEmail}`,
    secureLinkId,
    name: displayName,
    email: normalizedEmail,
    role: DeckPermissionsUserRole.Viewer,
    initials: getInitials(displayName),
    isCurrentUser: false,
    avatarColor: '',
    isPending,
    inviteType: 'external',
  }
}

function secureLinkToExternalInviteUser(secureLink: APISecureLink) {
  return createExternalInviteUser({
    secureLinkId: secureLink.id,
    email: secureLink.email_addresses[0] ?? '',
    name: secureLink.name,
  })
}

function externalShareToExternalInviteUser(share: APIDeckExternalShare): ExternalInviteUser {
  return createExternalInviteUser({
    secureLinkId: String(share.id),
    email: share.email,
  })
}

function cloneExternalUsers(usersToClone: ExternalInviteUser[]) {
  return usersToClone.map(user => ({ ...user }))
}

function upsertSavedExternalUser(user: ExternalInviteUser) {
  savedExternalUsers.value = [
    ...savedExternalUsers.value.filter(
      existing => existing.email.toLowerCase() !== user.email.toLowerCase() && existing.secureLinkId !== user.secureLinkId,
    ),
    user,
  ]
}

function removeSavedExternalUser({ secureLinkId, email }: { secureLinkId?: string | null; email?: string }) {
  savedExternalUsers.value = savedExternalUsers.value.filter(existing => {
    if (secureLinkId && existing.secureLinkId === secureLinkId) return false
    if (email && existing.email.toLowerCase() === email.toLowerCase()) return false
    return true
  })
}

function syncCurrentExternalUsersFromSaved() {
  externalUsers.value = cloneExternalUsers(savedExternalUsers.value)
}

function isExternalUser(user: ShareAccessUser): user is ExternalInviteUser {
  return 'inviteType' in user && user.inviteType === 'external'
}

function getRoleOption(role: DeckPermissionsUserRole) {
  return roleOptions.value.find(option => option.id === role) || null
}

function getGeneralAccessOption(option: GeneralAccessOption | null) {
  if (!option) return null
  return generalAccessDropdownOptions.value.find(opt => opt.id === option.value) || null
}

function getGeneralAccessOptionById(id: string): GeneralAccessOption | null {
  return generalAccessOptions.value.find(option => option.value === id) || null
}

function addToAccessChangeQueue(item: AccessChangeQueueItem) {
  if (accessChangeQueue.value.some(p => p.key === item.key)) {
    accessChangeQueue.value = accessChangeQueue.value.map(p => (p.key === item.key ? { ...item } : p))
  } else {
    accessChangeQueue.value.push(item)
  }
}

function removeFromAccessChangeQueue(key: string) {
  accessChangeQueue.value = accessChangeQueue.value.filter(item => item.key !== key)
}

function syncGeneralAccessUIFromPermissions() {
  const baseAccess = permissions.value?.base_access
  const normalizedAccess = baseAccess === DeckBaseAccess.CompanyEdit ? DeckBaseAccess.CompanyView : baseAccess
  selectedGeneralAccess.value = generalAccessOptions.value.find(option => option.value === normalizedAccess) ?? null
  deckPermissionUserRole.value = permissionRoleMap.get(baseAccess as DeckBaseAccess) || DeckPermissionsUserRole.Viewer
}

function enqueueGeneralAccessChange(option: GeneralAccessOption) {
  selectedGeneralAccess.value = option
  addToAccessChangeQueue({
    key: 'general-access',
    userId: null,
    type: AccessChangeType.GeneralAccess,
    promise: () =>
      updateDeckPermissions({
        deckId: props.deck.id,
        companyId: props.deck.company_id,
        baseAccess: option.value,
      }),
  })
  deckPermissionUserRole.value = DeckPermissionsUserRole.Viewer
}

function commitPendingGeneralAccessToQueue() {
  const option = pendingAccessChange.value
  if (!option) return
  pendingAccessChange.value = null
  enqueueGeneralAccessChange(option)
}

// Sort users with current user first, then alphabetically
const sortedUsers = computed<ShareAccessUser[]>(() => {
  return [...users.value, ...externalUsers.value].filter(user => Boolean(user.email) && Boolean(user.name)).sort((a, b) => {
    if (a.isCurrentUser && !b.isCurrentUser) return -1
    if (!a.isCurrentUser && b.isCurrentUser) return 1
    return a.name?.localeCompare(b.name ?? '') ?? 0
  })
})
const elevatedCount = computed(() => users.value.filter(user => isElevatedDeckRole(user.role)).length)

const canManageAccess = computed(() => isElevatedDeckRole(userDeckRole.value) || hasRole('super_admin'))

function isDeckCreator(user: any) {
  if (isExternalUser(user)) {
    return false
  }

  const creatorId = props.deck.created_by?.id
  return creatorId != null && user.user_id === creatorId
}

const shouldDisableRoleDropdown = computed(() => (user: ShareAccessUser) => {
  if (isDeckCreator(user)) {
    return true
  }

  if (user.isCurrentUser && isElevatedDeckRole(user.role) && elevatedCount.value === 1) {
    return true
  }

  return loading.value || !canManageAccess.value
})

function shouldBeDisabledForAccessRow(user: ShareAccessUser) {
  if (isExternalUser(user)) {
    return loading.value || !canManageAccess.value
  }

  if (hasRole('super_admin')) {
    return false
  }

  if (!isElevatedDeckRole(userDeckRole.value)) {
    return true
  }

  if (isElevatedDeckRole(user.role) && elevatedCount.value < 2) {
    return true
  }

  return false
}

const canRemoveUser = computed(() => (user: ShareAccessUser) => {
  if (isExternalUser(user)) {
    return !shouldBeDisabledForAccessRow(user)
  }

  if (isDeckCreator(user) && !hasRole('super_admin')) {
    return false
  }

  if (user.isCurrentUser && hasRole('company_admin')) {
    return false
  }

  if (user.isCurrentUser && isElevatedDeckRole(user.role)) {
    return elevatedCount.value > 1
  }

  return !shouldBeDisabledForAccessRow(user) && (!user.isCurrentUser || hasRole('super_admin'))
})

const canResendInvite = computed(() => (user: ShareAccessUser) => {
  return isExternalUser(user) && !!user.secureLinkId && !user.isPending && canManageAccess.value && !loading.value
})

const showMultipleOwnersBanner = computed(() => {
  const hasMultipleElevated = elevatedCount.value > 1
  const isPrivate = selectedGeneralAccess.value?.value === DeckBaseAccess.Private
  return !loading.value && !hasMultipleElevated && isPrivate
})

async function handleGeneralAccessSelect(option: GeneralAccessOption | null) {
  if (!option) return

  $analytics.track('deckShareDialogGeneralAccessSelect', { option: option.value })
  const currentAccess = selectedGeneralAccess.value?.value
  const isGoingFromPublicToPrivate = currentAccess === DeckBaseAccess.Public && option.value !== DeckBaseAccess.Public

  if (isGoingFromPublicToPrivate) {
    pendingAccessChange.value = option
    await openEmbedWarningDialog()
  } else {
    enqueueGeneralAccessChange(option)
  }
}

function handleDeckPermissionAccess(role: DeckBaseAccess) {
  deckPermissionUserRole.value = permissionRoleMap.get(role) as DeckPermissionsUserRole
  addToAccessChangeQueue({
    key: 'general-access',
    userId: null,
    type: AccessChangeType.GeneralAccess,
    promise: () =>
      updateDeckPermissions({
        deckId: props.deck.id,
        companyId: props.deck.company_id,
        baseAccess: role,
      }),
  })
}

function handleInviteRequested(invite: InviteRequestedPayload) {
  if (invite.type === 'internal' && invite.user) {
    const pendingUser = createPermissionUser(
      {
        user_id: invite.user.id,
        name: invite.user.name,
        email: invite.user.email_address || invite.email,
        role: DeckPermissionsUserRole.Viewer,
      },
      true,
    )

    users.value = [...users.value.filter(user => user.user_id !== pendingUser.user_id), pendingUser]
    addToAccessChangeQueue({
      key: pendingUser.key,
      userId: pendingUser.user_id,
      type: AccessChangeType.Upsert,
      promise: () =>
        upsertDeckUserPermissions({
          deckId: props.deck.id,
          companyId: props.deck.company_id,
          user_id: pendingUser.user_id,
          role: pendingUser.role,
        }),
    })

    return
  }

  const pendingUser = createExternalInviteUser({ email: invite.email, isPending: true })
  externalUsers.value = [...externalUsers.value.filter(user => user.email.toLowerCase() !== pendingUser.email.toLowerCase()), pendingUser]
  addToAccessChangeQueue({
    key: pendingUser.key,
    userId: null,
    type: AccessChangeType.SecureLinkCreate,
    promise: () =>
      requestDeckSecureLink({
        deckId: props.deck.id,
        companyId: props.deck.company_id,
        email: pendingUser.email,
      }),
    onSuccess: value => {
      const secureLinkUser = secureLinkToExternalInviteUser(value as APISecureLink)
      upsertSavedExternalUser(secureLinkUser)
    },
  })
}

async function handleUserRoleSelect({ user, role }: { user: ShareAccessUser; role: DeckPermissionsUserRole }) {
  if (isExternalUser(user)) return

  const apiRole = apiRoleFromSharePickerRole(role)
  $analytics.track('deckShareDialogUserRoleSelect', { user_id: user.user_id, role: apiRole })
  const currentUserRole = users.value.find(u => u.user_id === user.user_id)?.role
  if (isElevatedDeckRole(currentUserRole) && role === DeckPermissionsUserRole.Viewer && user.isCurrentUser) {
    const confirmed = await new Promise<boolean>(resolve => {
      editorDowngradeResolver = resolve
      editorDowngradeAlertShowing.value = true
    })
    if (!confirmed) return
  }

  addToAccessChangeQueue({
    key: user.key,
    userId: user.user_id,
    type: AccessChangeType.Upsert,
    promise: () =>
      upsertDeckUserPermissions({
        deckId: props.deck.id,
        companyId: props.deck.company_id,
        user_id: user.user_id,
        role: apiRole,
      }),
  })

  const row = users.value.find(u => u.user_id === user.user_id)
  if (row) row.role = apiRole
}

async function fetchDeckPermissions() {
  // Get the deck permissions
  permissions.value = await getDeckPermissions({
    deckId: props.deck.id,
    companyId: props.deck.company_id,
  })

  // Map the data for some convenience props
  // cleanupParticipantColors(permissions.value?.roles.map(role => role.user_id).filter((id): id is number => id !== undefined) || [])
  if (permissions.value) {
    users.value = permissions.value.roles.map(role => createPermissionUser(role))

    const fetchedExternalUsers = (permissions.value.external_shares ?? []).map(externalShareToExternalInviteUser)
    externalUsers.value = fetchedExternalUsers
    savedExternalUsers.value = cloneExternalUsers(fetchedExternalUsers)
  } else {
    users.value = []
    externalUsers.value = []
    savedExternalUsers.value = []
  }

  // Set the user's deck role
  userDeckRole.value = users.value.find(user => user.isCurrentUser)?.role ?? null

  syncGeneralAccessUIFromPermissions()
}

async function handleResendInvite(user: ShareAccessUser) {
  if (!isExternalUser(user) || !user.secureLinkId) return

  loading.value = true
  try {
    const secureLink = await resendDeckSecureLink({ id: user.secureLinkId })
    const updatedUser = secureLinkToExternalInviteUser(secureLink)

    externalUsers.value = externalUsers.value.map(existing =>
      existing.secureLinkId === user.secureLinkId || existing.email.toLowerCase() === user.email.toLowerCase() ? updatedUser : existing,
    )
    upsertSavedExternalUser(updatedUser)

    setNotification({
      type: 'success',
      title: 'Invite Resent',
      text: `We resent the invite to ${user.email}.`,
      delay: 5000,
    })
  } catch (error) {
    Sentry.captureException(error)
    setNotification({
      type: 'danger',
      title: 'Resend Failed',
      text: `We couldn't resend the invite to ${user.email}. Please try again.`,
      delay: 5000,
    })
  } finally {
    loading.value = false
  }
}

function handleDeleteUser(user: ShareAccessUser) {
  if (isExternalUser(user)) {
    if (!user.secureLinkId) {
      removeFromAccessChangeQueue(user.key)
      externalUsers.value = externalUsers.value.filter(existing => existing.email.toLowerCase() !== user.email.toLowerCase())
      return
    }

    addToAccessChangeQueue({
      key: `secure-link:${user.secureLinkId}`,
      userId: null,
      type: AccessChangeType.SecureLinkDelete,
      promise: () => deleteDeckSecureLink({ id: user.secureLinkId! }),
      onSuccess: () => {
        removeSavedExternalUser({ secureLinkId: user.secureLinkId, email: user.email })
      },
    })
    externalUsers.value = externalUsers.value.filter(existing => existing.secureLinkId !== user.secureLinkId)
    return
  }

  if (user.isPending) {
    removeFromAccessChangeQueue(user.key)
    users.value = users.value.filter(existing => existing.user_id !== user.user_id)
    return
  }

  addToAccessChangeQueue({
    key: user.key,
    userId: user.user_id,
    type: AccessChangeType.Delete,
    promise: () =>
      deleteDeckUserPermissions({
        deckId: props.deck.id,
        companyId: props.deck.company_id,
        user_id: user.user_id,
      }),
  })
  users.value = users.value.filter(u => u.user_id !== user.user_id)
}

onMounted(async () => {
  loading.value = true

  await fetchDeckPermissions()

  // Fetch embeds for this deck
  await fetchEmbeds()

  loading.value = false
})

async function fetchEmbeds() {
  try {
    const deckId = props.deck.id
    const companyId = props.deck.company_id
    if (deckId && companyId) {
      // const { getCompanyEmbedDecks } = useCompanyEmbedDecks(companyId)
      // embeds.value = await getCompanyEmbedDecks(deckId)
    }
  } catch (error) {
    embeds.value = []
    Sentry.captureException(error)
  }
}

const openEmbedWarningDialog = async () => {
  if (props.deck.id && props.deck.company_id) {
    try {
      loading.value = true
      const decksAffected = await getUsedEmbedDecks({ deckId: props.deck.id, companyId: props.deck.company_id })
      const publicDecks = await getPublicDecks(props.deck.company_id)

      if (decksAffected.length) {
        Object.assign(embedUsageWarning, {
          showing: !!decksAffected.length,
          decksAffected,
          publicDecks,
          confirm: async (selectedDeck: any) => {
            try {
              if (selectedDeck) {
                const replacementDeck = publicDecks.find(d => d.id === selectedDeck.id)
                if (replacementDeck) {
                  await updateAllEmbeds({ decks: decksAffected, deck: replacementDeck })
                }
              }
              embedUsageWarning.showing = false
              await openDrillLabelWarningDialog()
            } catch (e) {
              Sentry.captureException(e)
            }
          },
          cancel: () => {
            embedUsageWarning.showing = false
            pendingAccessChange.value = null
          },
        })
        return
      }

      await openDrillLabelWarningDialog()
    } finally {
      loading.value = false
    }
  }
}

const openDrillLabelWarningDialog = async () => {
  if (!props.drillDataSetManager || !props.slides) {
    commitPendingGeneralAccessToQueue()
    return
  }

  try {
    loading.value = true
    const modelIds = extractModelIds()
    const privateOrSecureDrillData: Drill[] = Array.from(
      new Set(
        (await getDrillLabels(modelIds))
          .filter(drill => drill.data_set.view_access_type === 'private' || drill.data_set.view_access_type === 'secure')
          .map(drill => JSON.stringify(drill.data_set)),
      ),
    ).map(x => JSON.parse(x))

    if (privateOrSecureDrillData.length) {
      Object.assign(drillDataAccessTypeWarning, {
        showing: true,
        drillData: privateOrSecureDrillData,
        cancel: () => {
          drillDataAccessTypeWarning.showing = false
          pendingAccessChange.value = null
        },
        confirm: async () => {
          try {
            await setDrillDataPublic(privateOrSecureDrillData)
            drillDataAccessTypeWarning.showing = false
            commitPendingGeneralAccessToQueue()
          } catch (e) {
            Sentry.captureException(e)
          }
        },
        secondaryAction: async () => {
          drillDataAccessTypeWarning.showing = false
          commitPendingGeneralAccessToQueue()
        },
      })

      return
    }

    commitPendingGeneralAccessToQueue()
  } finally {
    loading.value = false
  }
}

async function onSaveChanges() {
  if (!accessChangeQueue.value.length) return
  loading.value = true
  try {
    const queuedItems = [...accessChangeQueue.value]
    const results = await Promise.allSettled(queuedItems.map(({ promise }) => promise()))
    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        queuedItems[index].onSuccess?.(result.value)
      }
    })
    const rejected = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected')
    if (rejected.length) {
      for (const r of rejected) {
        if (r.reason) Sentry.captureException(r.reason)
      }
      setNotification({
        type: 'danger',
        title: 'Error',
        text: 'Some changes were not saved. Please check your settings and try again.',
        delay: 5000,
      })
    } else {
      setNotification({
        type: 'success',
        title: 'Changes Saved',
        text: 'The changes have been saved.',
        delay: 5000,
      })
      emit('refresh-deck')
    }
  } catch (error) {
    Sentry.captureException(error)
    setNotification({
      type: 'danger',
      title: 'Error',
      text: 'Some changes were not saved. Please check your settings and try again.',
      delay: 5000,
    })
  } finally {
    accessChangeQueue.value = []
    syncCurrentExternalUsersFromSaved()
    loading.value = false
    void fetchDeckPermissions().catch(error => {
      Sentry.captureException(error)
    })
  }
}

function onShareDialogOpenChange(next: boolean) {
  if (next) {
    open.value = true
    return
  }
  if (accessChangeQueue.value.length === 0) {
    open.value = false
    return
  }
  if (unsavedChangesAlertShowing.value) return
  unsavedChangesAlertShowing.value = true
}

async function onUnsavedChangesDiscard() {
  unsavedChangesAlertShowing.value = false
  accessChangeQueue.value = []
  syncCurrentExternalUsersFromSaved()
  loading.value = true
  try {
    await fetchDeckPermissions()
  } finally {
    loading.value = false
    open.value = false
  }
}

function onUnsavedChangesAlertUpdate(showing: boolean) {
  unsavedChangesAlertShowing.value = showing
}

function onEditorDowngradeConfirm() {
  const r = editorDowngradeResolver
  editorDowngradeResolver = null
  editorDowngradeAlertShowing.value = false
  r?.(true)
}

function onEditorDowngradeAlertUpdate(showing: boolean) {
  if (!showing) {
    editorDowngradeAlertShowing.value = false
    if (editorDowngradeResolver) {
      editorDowngradeResolver(false)
      editorDowngradeResolver = null
    }
  }
}

async function getDrillLabels(modelIds: number[]) {
  let dataSets: DrillDataSet[] = []
  if (props.deck.company_id && props.drillDataSetManager) {
    dataSets = (
      await Promise.all(
        modelIds.map(modelId => {
          return props.drillDataSetManager!.getDataSets({ modelId, companyId: props.deck.company_id })
        }),
      )
    ).flatMap(x => x)
  }
  return dataSets
}

function extractModelIds() {
  if (!props.slides) return []
  return [...new Set(props.slides.map(s => s.model_id).filter(x => !!x))] as number[]
}

async function setDrillDataPublic(drillData: Drill[]) {
  await Promise.all(
    drillData.map(async drill => {
      await $fetch<any>(`v2/drill/companies/${props.deck.company_id}/data/${drill.id}`, {
        ...useFetchSharedOptions(),
        method: 'PATCH',
        body: {
          view_access_type: 'public',
        },
      })
    }),
  )
}

async function getUsedEmbedDecks({ deckId, companyId }: { deckId: string | number; companyId: string | number }) {
  const { results } = await $fetch<any>(`v2/companies/${companyId}/embed/decks`, {
    ...useFetchSharedOptions(),
    params: { deck_id: deckId, include: ['partner_name'] },
  })
  return results
}

function hasModelTeaserPair(slides: Slide[]): boolean {
  const counts = new Map<number, number>()
  for (const s of slides) {
    if (s.type === 'M' && s.model_id != null) {
      counts.set(s.model_id, (counts.get(s.model_id) ?? 0) + 1)
    }
  }
  return [...counts.values()].some(c => c >= 2)
}

function hasAssetTeaserPair(slides: Slide[]): boolean {
  const counts = new Map<number, number>()
  for (const s of slides) {
    if (s.type === 'W' && 'asset_id' in s && s.asset_id != null) {
      counts.set(s.asset_id, (counts.get(s.asset_id) ?? 0) + 1)
    }
  }
  return [...counts.values()].some(c => c >= 2)
}

function hasSiteOrUnity360Slide(slides: Slide[]): boolean {
  return slides.some(s => s.type === 'S' || s.type === 'X')
}

function isValidEmbedReplacementDeck(slides: Slide[] | undefined): boolean {
  if (!slides?.length) return false
  return hasModelTeaserPair(slides) || hasAssetTeaserPair(slides) || hasSiteOrUnity360Slide(slides)
}

async function getPublicDecks(companyId: string | number) {
  const { results } = await $fetch<{ results: Deck[] }>(`v2/companies/${companyId}/decks`, {
    ...useFetchSharedOptions(),
    params: {
      base_access: 'public',
      archived: false,
      limit: 1000,
      include: ['slides'],
    },
  })

  return results.filter(deck => isValidEmbedReplacementDeck(deck.slides))
}

async function updateAllEmbeds({ decks, deck }: { decks: DeckWithEmbed[]; deck: any }) {
  const [start_slide_id, end_slide_id] = getDeckSlideIds(deck)

  await Promise.all(
    decks.map(async (embeddedDeck: DeckWithEmbed) => {
      await $fetch<any>(`v2/companies/${deck.company_id}/embed/decks/${embeddedDeck.id}`, {
        ...useFetchSharedOptions(),
        method: 'PATCH',
        body: {
          start_slide_id,
          end_slide_id,
          company_id: deck.company_id,
          deck_id: deck.id,
        },
      })
    }),
  )
}

function getDeckSlideIds(deck: Deck & { slides?: Slide[] }): [number | undefined, number | undefined] {
  const slides = deck.slides ?? []
  if (!slides.length) return [undefined, undefined]

  const wSlidesSeen = new Map<number, Slide[]>()
  for (const s of slides) {
    if (s.type !== 'W' || !('asset_id' in s) || s.asset_id == null) continue
    const list = wSlidesSeen.get(s.asset_id) ?? []
    list.push(s)
    wSlidesSeen.set(s.asset_id, list)
    if (list.length === 2) {
      return [list[0].id, list[1].id]
    }
  }

  const modelSlidesSeen = new Map<number, Slide[]>()
  for (const s of slides) {
    if (s.type !== 'M' || s.model_id == null) continue
    const list = modelSlidesSeen.get(s.model_id) ?? []
    list.push(s)
    modelSlidesSeen.set(s.model_id, list)
    if (list.length === 2) {
      return [list[0].id, list[1].id]
    }
  }

  const siteSlide = slides.find(s => s.type === 'S')
  if (siteSlide) return [siteSlide.id, undefined]

  const threeSixtySlide = slides.find(s => s.type === 'X')
  if (threeSixtySlide) return [threeSixtySlide.id, undefined]

  return [undefined, undefined]
}
</script>
